import { useQueryClient } from '@tanstack/react-query';
import { isEqual, last } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { filter, tap } from 'rxjs';

import { QUERY_KEY_CONST } from '@/constant';
import {
  useDnDWorkDataJson,
  useFrameCount,
  useFrameDetail,
  useFrameRangeList,
  useSequentialFrameFetcher,
  useSyncStatisticQuery,
  useTaskDetailQuery,
  useUpdateExpireTimeMutation,
} from '@/hook';
import { useFrameSetMetaData } from '@/hook/tool/useFrameMeta';
import { trackIDManager } from '@/manager';
import { VFlex, useSubscriptions, useVToast } from '@/package';
import { useLabelingBoxContext, useWebSocket } from '@/provider';
import {
  useAuthStore,
  usePcdRenderingStore,
  useReviewStore,
  useTaskFrameStore,
  useToolMenuStore,
  useToolPermissionStore,
} from '@/store';
import type { FrameData } from '@/type';
import appWorker from '@/worker';

import ToolMain from '../ToolMain';
import WorkState from '../WorkState';
import FrameDataAutoSaver from './FrameDataAutoSaver';
import ToolDevTool from './ToolDevTool';
import { useTrackIDChangeRequestBootstrap } from './useTrackIDChangeRequestBootstrap';
import useWorkTimeTracker from './useWorkTimeTracker';

const ToolBootstrap = ({
  projectID,
  taskID,
  frameSeq,
}: {
  projectID: number;
  taskID: number;
  frameSeq: number;
}) => {
  const queryClient = useQueryClient();
  const { addToast } = useVToast();
  const selectedTask = useTaskFrameStore(state => state.selectedTask!);
  const { data: taskDetail, isLoading: isTaskDetailLoading } =
    useTaskDetailQuery(selectedTask!.id);

  const {
    currentFrame,
    clear: clearTaskFrameStore,
    hasFrame,
    isAssignedMe,
    isAssignedReviewMe,
    isValidFrameDetail,
    isWorkState,
    setCurrentFrame,
    setFrame,
    selectTask,
  } = useTaskFrameStore();
  const { permission, setPermission } = useToolPermissionStore();
  const { alarmMessage$ } = useWebSocket();

  const frameDetailResult = useFrameDetail({ taskID, frameSeq });
  const { frameDetail } = frameDetailResult;

  const {
    pcdIndexBoxListMap,
    clear: clearLabelingBoxListStore,
    clearSelectBoxList,
    setBoxList,
  } = useLabelingBoxContext();
  const { setComment } = useReviewStore();
  const { mutate: syncStatistic } = useSyncStatisticQuery();
  const { mutateAsync: updateExpireTime } = useUpdateExpireTimeMutation();

  const workState = useMemo(
    () => currentFrame?.workState,
    [currentFrame?.workState],
  );
  const isUpsertLabelingReady = useRef(false);
  const isLoading = useMemo(
    () => frameDetailResult.isLoading || !!0,
    [frameDetailResult.isLoading],
  );
  const isDone = useMemo(() => isWorkState('Done'), [workState]);
  const isPrework = useMemo(() => isWorkState('Prework'), [workState]);
  const isReject = useMemo(() => isWorkState('Reject'), [workState]);
  const isReview = useMemo(() => isWorkState('Review'), [workState]);
  const isReviewing = useMemo(() => isWorkState('Reviewing'), [workState]);
  const isWorking = useMemo(() => isWorkState('Working'), [workState]);
  const {
    pcdIndex,
    setPcdIndex,
    clear: clearPcdRenderingStore,
  } = usePcdRenderingStore();
  const { clear: clearToolMenuStore } = useToolMenuStore();
  const fileFrameCount = selectedTask?.fileFrameCount ?? 0;

  const disabledBoxSync =
    // eslint-disable-next-line react-hooks/refs
    !isUpsertLabelingReady.current ||
    isLoading ||
    !permission.labeling.updatable ||
    (!isWorking && !isReviewing) ||
    (isWorking && !isAssignedMe()) ||
    (isReviewing && !isAssignedReviewMe()) ||
    !Array.isArray(pcdIndexBoxListMap[pcdIndex]) ||
    !!0;

  const handleSyncStatistic = useCallback((currentFrame: FrameData) => {
    if (
      currentFrame &&
      (currentFrame.workerID === useAuthStore.getState().user.id ||
        currentFrame.reviewerID === useAuthStore.getState().user.id)
    ) {
      syncStatistic({
        taskID: `${currentFrame.taskID}`,
        frameSeq: [`${currentFrame.frameSeq}`],
      });
    }
  }, []);

  const { $draggableRef } = useDnDWorkDataJson(boxList => {
    if (isAssignedMe()) {
      setBoxList(boxList);
    } else {
      addToast({
        autoClose: true,
        message: 'You are not assigned to this frame',
        variant: 'error',
      });
    }
  });

  useFrameCount({ projectID, taskID });
  useFrameRangeList({ fileFrameCount, frameSeq, taskID });
  useSequentialFrameFetcher({ frameSeq, taskID });
  useFrameSetMetaData(frameDetail);
  useTrackIDChangeRequestBootstrap();
  useWorkTimeTracker();

  useSubscriptions(
    () => [
      alarmMessage$
        .pipe(
          filter(payload => payload.alarmType === 'MiriTaskAIAnnotationDone'),
          filter(payload => payload.reportPayload.taskID == selectedTask?.id),
          tap(() => {
            frameDetailResult.refetch();
          }),
        )
        .subscribe(),
    ],
    [alarmMessage$, selectedTask?.id],
  );

  useEffect(() => {
    if (isTaskDetailLoading) return;

    selectTask(taskDetail);
  }, [isTaskDetailLoading, taskDetail]);

  useEffect(() => {
    isUpsertLabelingReady.current = false;

    setPcdIndex(frameSeq - 1);

    if (!trackIDManager.restore(`${taskID}-${frameSeq}`)) {
      trackIDManager.reset();
    }

    return () => {
      trackIDManager.save(`${taskID}-${frameSeq}`);
    };
  }, [frameSeq]);

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: QUERY_KEY_CONST.STATISTIC_KEYS.state(),
    });
  }, [frameSeq, projectID, taskID]);

  useEffect(() => {
    if (frameDetailResult.isLoading || isEqual(currentFrame, frameDetail))
      return;

    setCurrentFrame(frameDetail);
  }, [currentFrame, frameDetail, frameDetailResult.isLoading]);

  useEffect(() => {
    setComment(currentFrame?.reviewComment ?? '');
  }, [currentFrame?.reviewComment]);

  useEffect(() => {
    const lastOperation = last(selectedTask?.operation);

    if (
      lastOperation?.operationType === 'AIAnnotation' &&
      lastOperation.operationState === 'Running'
    ) {
      isUpsertLabelingReady.current = false;
    } else {
      isUpsertLabelingReady.current = true;
    }
  }, [currentFrame?.frameSeq, selectedTask?.operation]);

  useEffect(() => {
    if (isLoading && currentFrame == null) {
      setPermission({
        labeling: {
          readable: true,
          updatable: false,
          acceptable: false,
        },
        labelingReview: {
          readable: true,
          updatable: false,
          acceptable: false,
        },
        preAnnotation: {
          acceptable: false,
        },
        aiAssist: {
          acceptable: false,
        },
      });
    }
  }, [currentFrame, isLoading, setPermission]);

  useEffect(() => {
    return () => handleSyncStatistic(currentFrame as FrameData);
  }, [currentFrame?.frameSeq, currentFrame?.taskID]);

  useEffect(() => {
    const handleBeforeUnload = () =>
      handleSyncStatistic(currentFrame as FrameData);

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentFrame?.frameSeq, currentFrame?.taskID]);

  useEffect(() => {
    if (
      !currentFrame ||
      currentFrame.frameSeq - 1 !== pcdIndex ||
      hasFrame(pcdIndex)
    )
      return;

    setFrame(currentFrame, pcdIndex);
  }, [currentFrame, pcdIndex]);

  useEffect(() => {
    if (currentFrame?.frameSeq == null) return;

    clearSelectBoxList();
  }, [currentFrame?.frameSeq]);

  useEffect(() => {
    return () => {
      const selectedTask = useTaskFrameStore.getState().selectedTask;
      const latestTrackSession =
        useTaskFrameStore.getState().latestTrackSession;

      if (
        selectedTask &&
        latestTrackSession.id &&
        latestTrackSession.frameSeq
      ) {
        updateExpireTime({
          leaveFrame: true,
          frameSeq: latestTrackSession.frameSeq,
          taskID: selectedTask.id,
          trackSessionID: latestTrackSession.id,
        });
      }

      clearLabelingBoxListStore();
      clearPcdRenderingStore();
      clearTaskFrameStore();
      clearToolMenuStore();

      appWorker.request('pcd', 'clearQuadTreeCache');
    };
  }, []);

  return (
    <VFlex
      ref={$draggableRef}
      className="h-full w-full"
      items="center"
      justify="center"
    >
      {isValidFrameDetail() && (
        <>
          {(isPrework || isReject) && <WorkState.Prework />}
          {isWorking && isAssignedMe() && <WorkState.Working />}
          {isReview && <WorkState.Review />}
          {isReviewing && <WorkState.Reviewing />}
          {isDone && <WorkState.Done />}
        </>
      )}
      <ToolMain />
      {/* eslint-disable-next-line react-hooks/refs */}
      {!disabledBoxSync && (
        <FrameDataAutoSaver
          frameSeq={frameSeq}
          projectID={projectID}
          taskID={taskID}
        />
      )}
      <ToolDevTool />
    </VFlex>
  );
};

export default ToolBootstrap;
